import React, { useState } from "react";

import { UseMutationResult } from "@tanstack/react-query";
import { assignIDsToComponents } from "utils/utils";
import { Dashboard, DashboardRequest } from "api/model/dashboard/Dashboard";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { Button } from "primereact/button";
import { T } from "services/lang/LanguageService";
import useDashboardStore from "store/dashboard-store";
import { handleAddNewTab } from "./ImportDashboardOrTab";
import DashboardHistoryDialog from "./history/DashboardHistoryDialog";

interface DashboardEditorProps {
  updateQuery: UseMutationResult<Dashboard | undefined, Error, DashboardRequest, void>;
  dashboard: Dashboard;
  setDashboard: (dashboard: Dashboard) => void;
  tabId: number;
  triggerTabConfiguratorVisibility?: () => void;
  setReorderTabsDialogVisibility: (reorderTabsDialogVisibility: boolean) => void;
  setDashboardConfiguratorVisibility?: (dashboardConfiguratorVisibility: boolean) => void;
  setIsImportDashboardDialogVisibility?: (isImportDashboardDialogVisible: boolean) => void;
  setAddNewGroupDialogVisibility: (addNewGroupDialogVisibility: boolean) => void;
  isGroupReorderEnabled: boolean;
  setIsGroupReorderEnabled: (isGroupReorderEnabled: boolean) => void;
  setIsCreateDatasetDialogVisible?: (isCreateDatasetDialogVisible: boolean) => void;
  setDatasetSearchDialogVisibility?: (isDatasetSearchDialogVisible: boolean) => void;
  isReports?: boolean;
  setCurrentTabId?: (tabId: number) => void;
  navigate?: (url: string) => void;
  setShowReportSettings?: (showReportSettings: boolean) => void;
}

const DashboardEditor = (props: DashboardEditorProps) => {
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const isGridChanged = useDashboardStore(state => state.isGridChanged);

  const removeTabConfirmation = () => {
    easasConfirmDialog({
      icon: "pi pi-exclamation-triangle",
      header: T("action.remove-tab"),
      confirmedCallback: handleRemoveSelectedTab,
    });
  };

  const handleRemoveSelectedTab = () => {
    const updatedCurrentDashboard = {
      ...props.dashboard,
      dashboardTabsJs: [
        ...props.dashboard.dashboardTabsJs.slice(0, props.tabId),
        ...props.dashboard.dashboardTabsJs.slice(props.tabId + 1),
      ],
    };
    props.setDashboard(updatedCurrentDashboard);
    if (props.isReports) {
      props.setCurrentTabId?.(0);
    } else {
      props.navigate?.(`/dashboard/${props.dashboard?.id}/tab/0`);
    }
  };

  const handleSaveDashboard = () => {
    if (isGridChanged) {
      easasConfirmDialog({
        icon: "pi pi-exclamation-triangle",
        header: T("action.confirm-action"),
        message: T("confirm-dialog.dashboard-have-unsaved-grid-layout-changes"),
        confirmedCallback: () => {
          useDashboardStore.getState().triggerDashboardSave();
          props.updateQuery.mutate(assignIDsToComponents(props.dashboard));
        },
      });
    } else {
      props.updateQuery.mutate(assignIDsToComponents(props.dashboard));
    }
  };

  const buttonMainClassName = props.isReports ? "component-editor-button" : "dashboard-editor-button";

  return (
    <div className="flex flex-row justify-content-between gap-5 overflow-x-auto">
      <div className="flex flex-row gap-8 mb-1">
        <div className="flex flex-column gap-2 buttons-group-container">
          <div className="w-max">{T("word.dashboard")}</div>
          <div className="flex flex-row gap-2 mb-3">
            <Button
              icon="pi pi-save"
              tooltip={T("action.save-dashboard")}
              label={T("action.save")}
              className={`${buttonMainClassName} icon-blue`}
              tooltipOptions={{ position: "bottom" }}
              onClick={() => handleSaveDashboard()}
            />
            {props.setDashboardConfiguratorVisibility && (
              <Button
                icon="pi pi-cog"
                label={T("action.configure")}
                tooltip={T("action.configure-dashboard")}
                className={`${buttonMainClassName} icon-orange`}
                onClick={() => props.setDashboardConfiguratorVisibility?.(true)}
                tooltipOptions={{ position: "bottom" }}
              />
            )}
            <Button
              icon="pi pi-history"
              label={T("word.history")}
              tooltip={T("word.history")}
              className={`${buttonMainClassName} icon-blue`}
              onClick={() => setOpenHistoryDialog(true)}
              tooltipOptions={{ position: "bottom" }}
            />
            {props.setIsImportDashboardDialogVisibility && (
              <Button
                tooltip={T("action.import-dashboard-or-tab")}
                label={T("action.import")}
                icon="pi pi-file-import"
                tooltipOptions={{ position: "bottom" }}
                className={`${buttonMainClassName} icon-green`}
                onClick={() => props.setIsImportDashboardDialogVisibility?.(true)}
              />
            )}
          </div>
        </div>
        <div className="flex flex-column gap-2">
          <div className="w-max">{T("word.tabs")}</div>
          <div className="flex flex-row gap-2">
            <Button
              icon="pi pi-plus"
              label={T("action.add")}
              tooltip={T("action.add-new-tab")}
              className={`${buttonMainClassName} icon-green`}
              tooltipOptions={{ position: "bottom" }}
              onClick={() => handleAddNewTab(props.dashboard, props.setDashboard, "")}
            />
            {props.triggerTabConfiguratorVisibility && (
              <Button
                icon="pi pi-cog"
                label={T("action.configure")}
                tooltip={T("action.configure-tab")}
                className={`${buttonMainClassName} icon-orange`}
                onClick={() => props.triggerTabConfiguratorVisibility?.()}
                tooltipOptions={{ position: "bottom" }}
              />
            )}

            <Button
              tooltip={T("label.reorder-tabs")}
              label={T("action.reorder")}
              icon="pi pi-arrow-right-arrow-left"
              className={buttonMainClassName}
              onClick={() => props.setReorderTabsDialogVisibility(true)}
              tooltipOptions={{ position: "bottom" }}
            />
            <Button
              icon="pi pi-trash"
              label={T("action.remove")}
              tooltip={T("action.remove-tab")}
              tooltipOptions={{ position: "bottom" }}
              className={`${buttonMainClassName} icon-red`}
              onClick={removeTabConfirmation}
            />
          </div>
        </div>
        <div className="flex flex-column gap-2">
          <div className="w-max">{T("label.component-groups")}</div>
          <div className="flex flex-row justify-content-end gap-2">
            <Button
              tooltip={T("action.add-new-components-group")}
              label={T("action.add")}
              icon="pi pi-plus"
              className={`${buttonMainClassName} icon-green`}
              tooltipOptions={{ position: "bottom" }}
              onClick={() => props.setAddNewGroupDialogVisibility(true)}
            />
            <Button
              tooltip={T("action.reorder-components-groups")}
              label={T("action.reorder")}
              icon="pi pi-arrow-right-arrow-left"
              tooltipOptions={{ position: "bottom" }}
              className={buttonMainClassName}
              onClick={() => props.setIsGroupReorderEnabled(!props.isGroupReorderEnabled)}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-column gap-2">
        {props.isReports ? (
          <div className="w-max">{T("word.settings")}</div>
        ) : (
          <div className="w-max">{T("word.datasets")}</div>
        )}
        <div className="flex flex-row gap-2">
          {props.setShowReportSettings && (
            <Button
              label={T("word.report-settings")}
              icon="pi pi-pencil"
              className={`${buttonMainClassName} icon-blue`}
              onClick={() => props.setShowReportSettings?.(true)}
            />
          )}
          {props.setIsCreateDatasetDialogVisible && (
            <Button
              icon="pi pi-plus"
              label={T("action.create")}
              tooltip={T("action.create-new-dataset")}
              className={`${buttonMainClassName} icon-green`}
              tooltipOptions={{ position: "bottom" }}
              onClick={() => props.setIsCreateDatasetDialogVisible?.(true)}
            />
          )}
          {props.setDatasetSearchDialogVisibility && (
            <Button
              icon="pi pi-search"
              label={T("action.search")}
              tooltip={T("action.search-dataset")}
              className={`${buttonMainClassName} icon-purple`}
              tooltipOptions={{ position: "bottom" }}
              onClick={() => props.setDatasetSearchDialogVisibility?.(true)}
            />
          )}
        </div>
      </div>
      <DashboardHistoryDialog openDialog={openHistoryDialog} setOpenDialog={setOpenHistoryDialog} />
    </div>
  );
};

export default DashboardEditor;
